import { Component, computed, inject, Injector, input, InputSignal, runInInjectionContext } from '@angular/core';
import { ModalBuilder } from '../zn-modal-wrapper/services/modal-builder';
import { IAsideConfigContext, IModal, IModalOpenContext } from '../zn-modal-wrapper/interfaces/IModal';
import { ZnButton } from '../zn-button/zn-button';
import { NgTemplateOutlet } from '@angular/common';
import { IActionBase, IActionModal, IActionTrigger } from './interfaces/IAction';
import { ActionOverRecordTypes } from './enums/action-over-record-types';
import { ActionTypes } from './enums/action-type';
import { ZendraLocalizationServices } from '../enums/abpLocalizationServices';
import { LocalizationPipe } from '@abp/ng.core';
import { isObservable } from 'rxjs';

@Component({
  selector: 'zn-other-actions',
  imports: [ZnButton, NgTemplateOutlet, LocalizationPipe],
  templateUrl: './zn-other-actions.html',
  styleUrl: './zn-other-actions.scss',
})
export class ZnOtherActions {
  actionsOverTable: InputSignal<IActionBase[]> = input([]);
  actionsMultipleRecord: InputSignal<IActionBase[]> = input([]);
  actionsSingleRecord: InputSignal<IActionBase[]> = input([]);
  selectedRecords: InputSignal<any[]> = input([]);

  localizationResource = ZendraLocalizationServices.LanguageService;

  enableMultipleRecordActions = computed(() => {
    return this.selectedRecords().length >= 1;
  });

  enableSingleRecordActions = computed(() => {
    return this.selectedRecords().length === 1;
  });
  
  private readonly modalService: ModalBuilder = inject(ModalBuilder);
  private readonly injector = inject(Injector);

  open(config?: IModal) {
    this.modalService.openModal(
      config.component,
      config
    );
  }

  private buildSelectionInputs(actionOverRecordType: ActionOverRecordTypes): Record<string, any> {
    const selectedRecords = this.selectedRecords();

    if (
      actionOverRecordType === ActionOverRecordTypes.OverTable ||
      selectedRecords.length === 0
    ) {
      return {};
    }

    return {
      selectedRecords,
      selectedRecordIds: selectedRecords
        .map(record => record?.id)
        .filter((id): id is number => typeof id === 'number'),
    };
  }

  private resolveModalConfig(action: IActionModal): IModal {
    const selectionInputs = this.buildSelectionInputs(action.actionOverRecordType);
    const asideConfig = this.resolveAsideConfig(action, selectionInputs);
    const onCompleted = action.onCompleted;

    return {
      ...action.config,
      asideConfig,
      onCompleted: () => {
        action.config.onCompleted?.();
        onCompleted?.();
      },
      inputs: {
        ...action.config.inputs,
        ...selectionInputs,
      },
    };
  }

  private resolveAsideConfig(
    action: IActionModal,
    selectionInputs: Record<string, any>,
  ) {
    if (!action.config.asideConfigFactory) {
      return action.config.asideConfig;
    }

    const asideConfigContext: IAsideConfigContext = {
      selectedRecords: selectionInputs['selectedRecords'] ?? [],
      selectedRecordIds: selectionInputs['selectedRecordIds'] ?? [],
    };

    return action.config.asideConfigFactory(asideConfigContext) ?? action.config.asideConfig;
  }

  private executeTrigger(action: IActionTrigger): void {
    let execution: unknown;

    try {
      execution = action.execute?.();
    } catch (err) {
      console.error(err);
      return;
    }

    if (!action.afterSuccess) {
      if (isObservable(execution)) {
        execution.subscribe({
          complete: () => action.onCompleted?.(),
          error: err => console.error(err),
        });
        return;
      }

      if (this.isPromiseLike(execution)) {
        void execution
          .then(() => action.onCompleted?.())
          .catch(err => console.error(err));
      }

      return;
    }

    if (isObservable(execution)) {
      let lastResult: unknown;

      execution.subscribe({
        next: result => {
          lastResult = result;
        },
        complete: () => this.executeTriggerAfterSuccess(action, lastResult),
        error: err => console.error(err),
      });
      return;
    }

    if (this.isPromiseLike(execution)) {
      void execution
        .then(result => this.executeTriggerAfterSuccess(action, result))
        .catch(err => console.error(err));
      return;
    }

    this.executeTriggerAfterSuccess(action, execution);
  }

  private executeTriggerAfterSuccess(
    action: IActionTrigger,
    result: unknown,
  ): void {
    let afterSuccessExecution: unknown;

    try {
      afterSuccessExecution = action.afterSuccess?.(result);
    } catch (err) {
      console.error(err);
      action.onCompleted?.();
      return;
    }

    if (isObservable(afterSuccessExecution)) {
      afterSuccessExecution.subscribe({
        complete: () => action.onCompleted?.(),
        error: err => {
          console.error(err);
          action.onCompleted?.();
        },
      });
      return;
    }

    if (this.isPromiseLike(afterSuccessExecution)) {
      void afterSuccessExecution
        .then(() => action.onCompleted?.())
        .catch(err => {
          console.error(err);
          action.onCompleted?.();
        });
      return;
    }

    action.onCompleted?.();
  }

  private executeModalBeforeOpen(config: IModal) {
    if (!config.beforeOpen) {
      return;
    }

    const modalOpenContext: IModalOpenContext = {
      config,
      selectedRecords: config.inputs?.['selectedRecords'] ?? [],
      selectedRecordIds: config.inputs?.['selectedRecordIds'] ?? [],
    };

    return runInInjectionContext(this.injector, () => config.beforeOpen?.(modalOpenContext));
  }

  private openModalAction(action: IActionModal): void {
    const config = this.resolveModalConfig(action);
    const beforeOpenExecution = this.executeModalBeforeOpen(config);

    if (isObservable(beforeOpenExecution)) {
      beforeOpenExecution.subscribe({
        complete: () => this.open(config),
        error: err => console.error(err),
      });
      return;
    }

    if (this.isPromiseLike(beforeOpenExecution)) {
      void beforeOpenExecution
        .then(() => this.open(config))
        .catch(err => console.error(err));
      return;
    }

    this.open(config);
  }

  private isPromiseLike(value: unknown): value is Promise<unknown> {
    return !!value && typeof (value as Promise<unknown>).then === 'function';
  }

  executeAction(action: IActionBase | IActionModal | IActionTrigger) {
    if(action.actionOverRecordType === ActionOverRecordTypes.MultipleRecord && !this.enableMultipleRecordActions()) return;

    if(action.actionOverRecordType === ActionOverRecordTypes.SingleRecord && !this.enableSingleRecordActions()) return;

    if(action.actionType === ActionTypes.Modal) {
      this.openModalAction(action as IActionModal);
    } else {
      this.executeTrigger(action as IActionTrigger);
    }
  }
}
